import { useCallback, useEffect, useRef } from 'react';

/**
 * Slide/push clip for the sessions split, mirroring how the RHS session panel
 * hides: that aside animates its width over a fixed-width inner wrapper, so
 * the moving edge clips static content instead of reflowing it every frame.
 *
 * Both refs attach to the content wrappers inside the resizable panels (the
 * page owns the elements). `pinPanelContents` freezes them at fixed pixel
 * widths for the duration of a programmatic collapse/expand:
 *
 * - The list pins at the animation's target width (expand) or its current
 *   width (collapse), so the panel edge sweeps across static content.
 * - The detail pins at the wider of its current and target widths. Its content
 *   (the note canvas) is expensive to lay out — reflowing it per animation
 *   frame blocks the main thread and eats the transition, which is why the
 *   collapse used to look like a snap. Pinned wide, it lays out once and the
 *   animation only moves a clipped edge.
 *
 * `schedulePinRelease` returns the wrappers to fluid width after the animation
 * settles so handle drags resize normally. Direct style mutation (not state) —
 * the pin is a transient animation detail, and a render mid-animation must not
 * clear it.
 */
export const usePanelContentSlidePins = (settleMs: number) => {
  const leftContentRef = useRef<HTMLDivElement>(null);
  const rightContentRef = useRef<HTMLDivElement>(null);
  const releaseTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const releasePanelContents = useCallback(() => {
    clearTimeout(releaseTimerRef.current);
    releaseTimerRef.current = undefined;
    for (const el of [leftContentRef.current, rightContentRef.current]) {
      if (el) el.style.width = '';
    }
  }, []);

  /** Pins both content wrappers; returns whether anything was pinned. */
  const pinPanelContents = useCallback(
    ({ leftPx, rightPx }: { leftPx?: number; rightPx?: number }) => {
      let didPin = false;
      const left = leftContentRef.current;
      const leftWidth = leftPx ?? left?.offsetWidth ?? 0;
      if (left && leftWidth > 0) {
        left.style.width = `${Math.round(leftWidth)}px`;
        didPin = true;
      }
      const right = rightContentRef.current;
      if (right) {
        const rightWidth = Math.max(right.offsetWidth, rightPx ?? 0);
        if (rightWidth > 0) {
          right.style.width = `${Math.round(rightWidth)}px`;
          didPin = true;
        }
      }
      // Deliberate forced reflow: laying the pinned content out (the detail's
      // note canvas especially) can take hundreds of ms, and if the browser
      // does it lazily it lands in the same frame as the panel resize — the
      // transition clock starts under a blocked main thread and the slide
      // skips. Paying it synchronously here guarantees the animation frames
      // that follow only move a clipped edge. One read flushes the whole
      // dirty layout tree, so reading the detail (the expensive side) covers
      // the list pin too; the list is only read when there is no detail.
      if (didPin) void (right ?? leftContentRef.current)?.offsetWidth;
      return didPin;
    },
    []
  );

  /** Releases the pins once the animation window has passed. */
  const schedulePinRelease = useCallback(() => {
    if (releaseTimerRef.current) clearTimeout(releaseTimerRef.current);
    releaseTimerRef.current = setTimeout(releasePanelContents, settleMs);
  }, [releasePanelContents, settleMs]);

  useEffect(() => releasePanelContents, [releasePanelContents]);

  return {
    leftContentRef,
    rightContentRef,
    pinPanelContents,
    schedulePinRelease,
    releasePanelContents,
  };
};
